import './sliderEvents/multipleMedication';
import './sliderEvents/medicationSelector';
import storageService from '../services/storage';

/**
 * Class to handle the slider component
 */
class RadioSlider {
  /**
   * Constructor for the RadioSlider class
   *
   * @param {string} sliderKey The slider key to initialize the slider component
   */
  constructor(sliderKey) {
    this.callbackEvent = `${sliderKey}Callback`;
    this.slideChangeEvent = `${sliderKey}SlideChange`;
    this.answers = {};
    this.currentSlide = 0;
    this.slider = document.querySelector(`#${sliderKey}.slider-container .slider`);
    this.slides = this.slider.querySelectorAll('.slide');
    this.prevBtns = this.slider.querySelectorAll('.prevBtn');
    this.nextBtns = this.slider.querySelectorAll('.nextBtn');
    this.options = this.slider.querySelectorAll('.option-container');
    this.totalSlides = this.slides.length;

    this.init();
  }

  /**
   * Initialize the slider component
   */
  init() {
    this.resetSliderAnswers();
    this.addButtonEventListeners();
    this.addOptionEventListeners();
    this.dispatchSlideChange();
  }

  /**
   * Add event listeners to the slider options
   */
  addOptionEventListeners() {
    this.options.forEach((option) => {
      option.addEventListener('click', () => {
        this.slider.classList.remove('has-error');
      });
    });
  }

  /**
   * Add event listeners to the slider buttons
   */
  addButtonEventListeners() {
    this.prevBtns.forEach((prevBtn) => {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.movePrevSlide();
      });
    });

    this.nextBtns.forEach((nextBtn) => {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.moveNextSlide();
      });
    });
  }

  /**
   * Move the slider to the previous slide
   */
  movePrevSlide() {
    this.currentSlide--;
    this.slider.classList.remove('has-error');
    this.updateSliderPosition();
  }

  /**
   * Move the slider to the next slide
   */
  moveNextSlide() {
    const currentQuestion = `q${this.currentSlide + 1}`;
    const selectedOption = document.querySelector(`input[name="${currentQuestion}"]:checked`);

    if (!selectedOption) {
      this.slider.classList.add('has-error');

      return;
    }

    this.saveSelectedOption(currentQuestion, selectedOption);

    if (this.isLastSlide()) {
      document.dispatchEvent(new CustomEvent(this.callbackEvent));

      return;
    }

    this.currentSlide++;
    this.dispatchSlideChange();
    this.updateSliderPosition();
  }

  /**
   * Reset the slider answers in the local storage
   */
  resetSliderAnswers() {
    storageService().sliderAnswers = {};
  }

  /**
   * Check if the current slide is the last slide
   *
   * @returns {boolean} true if the current slide is the last slide, false otherwise
   */
  isLastSlide() {
    return this.currentSlide === this.totalSlides - 1;
  }

  /**
   * Save the selected option in the local storage
   *
   * @param {string} currentQuestion the current question key
   * @param {RadioNodeList} selectedOption the selected option element
   */
  saveSelectedOption(currentQuestion, selectedOption) {
    this.answers[currentQuestion] = JSON.parse(selectedOption.value);
    this.discardStaleAnswers();
    storageService().sliderAnswers = this.answers;
  }

  /**
   * Removes the answers of the slides after the current one.
   */
  discardStaleAnswers() {
    Object.keys(this.answers)
      .filter((question) => parseInt(question.slice(1)) > this.currentSlide + 1)
      .forEach((question) => delete this.answers[question]);
  }

  /**
   * Update the slider position to the current slide
   */
  updateSliderPosition() {
    const position = -this.currentSlide * 100;

    this.slider.style.transform = `translateX(${position}%)`;
  }

  /**
   * Notifies listeners that the current slide changed.
   *
   * Lets them react to it (e.g. update option content based on the answers selected so far) without the slider knowing
   * about them.
   */
  dispatchSlideChange() {
    const slide = this.slides[this.currentSlide];
    if (!slide) {
      return;
    }

    document.dispatchEvent(new CustomEvent(this.slideChangeEvent, { detail: { slide, answers: this.answers } }));
  }
}

window.RadioSlider = RadioSlider;
document.dispatchEvent(new CustomEvent('RadioSliderReady'));
